/**
 * This file is part of vue-boilerplate.
 * @link     : https://zhaiyiming.com/
 * @author   : Emil Zhai (root@derzh.com)
 * @modifier : Emil Zhai (root@derzh.com)
 * @copyright: Copyright (c) 2018 TINYMINS.
 */

import qs from 'querystringify';
import VueRouter, { Location, Route } from 'vue-router';
import { equals } from './util';
import { parsePostsRoute } from './secret';

export interface RouteInfo {
  fullPath?: string;
  hash?: string;
  meta?: Record<string, unknown>;
  path?: string;
  name?: string;
  params: Record<string, string>;
  query: Record<string, string>;
}

export interface NavLocationData {
  type: string;
  subType: string;
}

export type NavLocation =
  {
    mode: 'go';
    url: string;
  }
  | {
    mode: 'push' | 'replace';
    route: RouteInfo;
  }

export const routeEquals = (r1: Location, r2: Location, { ignores = {}, judges = {} }: {
  ignores?: {
    name?: string;
    params?: boolean | string[];
    query?: boolean | string[];
  };
  judges?: {
    name?: string;
    params?: string[];
    query?: string[];
  };
} = {}): boolean => {
  if (!r1 || !r2) {
    return false;
  }
  if (!ignores.name && r1.name !== r2.name) {
    return false;
  }
  if (ignores.query !== true && !equals(r1.query || {}, r2.query || {}, {
    judgeKeys: judges.query || [],
    ignoreKeys: ['reload', 'autoNav'].concat(ignores.query || []),
    ignoreValues: [void 0],
    strictType: false,
  })) {
    return false;
  }
  if (ignores.params !== true && !equals(r1.params || {}, r2.params || {}, {
    judgeKeys: judges.params || [],
    ignoreKeys: ['reload', 'autoNav'].concat(ignores.params || []),
    ignoreValues: [void 0],
    strictType: false,
  })) {
    return false;
  }
  return true;
};

export const routeClone = (r: Route | RouteInfo | RouteInfo): Required<RouteInfo> => {
  const route: Required<RouteInfo> = {
    fullPath: r.fullPath || '',
    hash: r.hash || '',
    meta: r.meta || {},
    name: r.name || '',
    params: {},
    query: {},
    path: r.path || '',
  };
  if (r.params) {
    Object.entries(r.params).forEach(([k, v]) => {
      if (v !== null && v !== void 0) {
        route.params[k] = String(v);
      }
    });
  }
  if (r.query) {
    Object.entries(r.query).forEach(([k, v]) => {
      if (v !== null && v !== void 0) {
        route.query[k] = String(v);
      }
    });
  }
  return route;
};

export const navigateLocation = (location: NavLocation, router: VueRouter): void => {
  if (location.mode === 'go' && location.url) {
    window.location.href = location.url;
  } else if ((location.mode === 'push' || location.mode === 'replace') && location.route) {
    if (location.mode === 'replace') {
      router.replace(location.route);
    } else {
      router.push(location.route);
    }
  }
};

export const parseNavLocation = ({ type, subType }: NavLocationData): NavLocation | undefined => {
  let location: NavLocation | undefined = void 0;
  if (type === 'web' || type === 'open') {
    location = {
      mode: 'go',
      url: subType,
    };
  } else if (type === 'page') {
    const r: RouteInfo = { params: {}, query: {} };
    switch (subType) {
      case 'tops':
        r.name = 'secret_rank';
        r.params.type = 'active';
        break;
      case 'sign':
      case 'signed':
        r.name = 'user_sign';
        break;
      case 'codes':
        r.name = 'point';
        break;
      case 'topics':
        r.name = 'discover_topics';
        break;
      case 'addon':
        r.name = 'jx3_assists';
        break;
      case 'lucky':
        r.name = 'secret_lucky';
        break;
      case 'pubg_chicken':
        r.name = 'pubg_chicken';
        break;
      case 'pubg_stats':
        r.name = 'pubg_stats';
        break;
      case 'play_hall':
        r.name = 'play_hall';
        break;
      case 'cities':
        r.name = 'secret_group_filters';
        r.params.group = 'cities';
        r.query.to = '/secret/posts/{{type}}/{{subType}}';
        break;
      case 'rooms':
        r.name = 'secret_group_filters';
        r.params.group = 'rooms';
        r.query.to = '/secret/{{type}}/{{subType}}';
        break;
      case 'vip':
        r.name = 'user_vip';
        break;
      case 'stuff':
        r.name = 'user_me';
        break;
      case 'share':
        r.name = 'user_share';
        break;
      case 'verify':
        r.name = 'user_verify';
        break;
      case 'wechat':
        r.name = 'user_wechat';
        break;
      case 'medals':
        r.name = 'user_medals';
        break;
      case 'subscription':
        r.name = 'subscription_config';
        break;
      default:
        if (subType.substr(0, 7) === 'topics_') {
          const id = subType.substr(7);
          if (id.match(/^\d+$/u)) {
            r.name = 'secret_room';
            r.params.id = id;
            r.params.subid = 'topics';
          } else {
            r.name = 'secret_list';
            r.params.p0 = 'topic';
            r.params.p1 = id;
          }
        } else if (subType.substr(0, 7) === 'topic_') {
          const [s = null, roomid = null, topic = null] = subType.substr(7).match(/(\d+)_(.+)/u) || [];
          if (s && roomid && topic) {
            r.name = 'secret_room';
            r.params.id = roomid.toString();
            r.params.subid = `topic_${topic}`;
          }
        }
        break;
    }
    if (r.name) {
      location = { mode: 'push', route: r };
    }
  } else if (type === 'filters') {
    location = {
      mode: 'push',
      route: {
        name: 'secret_group_filters',
        params: { group: subType },
        query: {},
      },
    };
  } else if (type === 'room') {
    location = {
      mode: 'push',
      route: {
        name: 'secret_list',
        params: { p0: 'room', p1: subType },
        query: {},
      },
    };
  } else if (type === 'posts') {
    location = {
      mode: 'push',
      route: parsePostsRoute(subType),
    };
  } else if (type === 'post') {
    const [id, query = ''] = subType.split('?');
    location = {
      mode: 'push',
      route: {
        name: 'secret_detail',
        params: { id },
        query: qs.parse(query),
      },
    };
  } else if (type === 'user') {
    location = {
      mode: 'push',
      route: {
        name: 'user_other',
        params: { id: subType },
        query: {},
      },
    };
  } else if (type === 'nav') {
    location = {
      mode: 'replace',
      route: subType as unknown as RouteInfo,
    };
  } else if (type === 'order') {
    location = {
      mode: 'push',
      route: {
        name: 'play_order_detail',
        params: { id: subType },
        query: {},
      },
    };
  } else if (type === 'service') {
    location = {
      mode: 'push',
      route: {
        name: 'play_service_detail',
        params: { id: subType },
        query: {},
      },
    };
  } else if (type === 'tone') {
    location = {
      mode: 'push',
      route: {
        name: 'tones_detail',
        params: { id: subType },
        query: {},
      },
    };
  }
  // 保证该函数如果返回的路由对象`to.route` 则必定包含完整的`params`和`query`对象 防止外部判断出错
  if (location && (location.mode === 'push' || location.mode === 'replace') && location.route) {
    if (!location.route.params) {
      location.route.params = {};
    }
    if (!location.route.query) {
      location.route.query = {};
    }
  }
  return location;
};

export const fillNavLocation = <T extends NavLocationData = NavLocationData>(data: T): { location?: NavLocation } & T => {
  const location = parseNavLocation(data);
  return Object.assign({}, data, { location });
};
